import React, { useState } from 'react';
import {
  IAuditoriaProvisaoIcmsResultado,
  IItemProvisaoIcms,
  IConciliadoProvisaoIcms,
} from '../interfaces/IAuditoriaProvisaoIcms';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaProvisaoIcmsResultado;
}


const formatPercent = (value?: number) =>
  ((value || 0) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-292: drill-down a nível de lançamento — todo ERRO precisa mostrar não só a carga
// tributária agregada do mês, mas os lançamentos reais (Receita + redutora de ICMS) que a formam.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemProvisaoIcms }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Origem</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.origem === 'ICMS' ? 'Redutora ICMS' : 'Receita'}</td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaProvisaoIcmsDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const anomaliasDetectadas = resultado.anomaliasDetectadas || [];
  const mesesConciliados = resultado.mesesConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Provisão Mensal de ICMS — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o faturamento do mês com anomalia, limitada a 1% da receita bruta do período (Art. 12, II, Lei nº 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Competências com carga tributária de ICMS anômala no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Anomalias na Provisão de ICMS</h3>
        <p className='text-muted'>
          Competências com carga tributária de ICMS (ICMS / faturamento) abaixo de 80% da média móvel dos 3 meses
          anteriores — indício de subfaturamento ou erro de lançamento na conta redutora.
        </p>

        {anomaliasDetectadas.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma anomalia detectada no período.</div>
        )}

        {anomaliasDetectadas.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th className='text-end'>Faturamento Base</th>
                  <th className='text-end'>Carga Atual</th>
                  <th className='text-end'>Carga de Referência (3M)</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {anomaliasDetectadas.map((item: IItemProvisaoIcms, idx) => {
                  const chave = `icms-${item.empresaId}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-alerta' onClick={() => alternarExpandir(chave)}>
                        <td><strong>{item.competencia}</strong></td>
                        <td className='text-end'>{formatCurrency(item.faturamentoBruto)}</td>
                        <td className='text-end'>{formatPercent(item.cargaTributariaReal)}</td>
                        <td className='text-end'>{formatPercent(item.mediaCargaTributariaReferencia)}</td>
                        <td className='text-end'>{formatCurrency(item.impactoFiscalEstimado)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fef2f2' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Alerta:</strong> Anomalia na Provisão de ICMS — carga tributária desproporcional à média histórica.</div>
                              <div><strong>Competência:</strong> {item.competencia}.</div>
                              <div><strong>Faturamento Base:</strong> {formatCurrency(item.faturamentoBruto)}.</div>
                              <div><strong>Carga Atual:</strong> {formatPercent(item.cargaTributariaReal)}.</div>
                              <div><strong>Carga de Referência (Média 3M):</strong> {formatPercent(item.mediaCargaTributariaReferencia)}.</div>
                              <div><strong>Multa ECD Potencial (5% limitada a 1% da receita bruta):</strong> {formatCurrency(item.impactoFiscalEstimado)}.</div>
                              <div><strong>Conta(s) de Receita:</strong> {item.contasReceita || 'não identificada(s)'}.</div>
                              <div><strong>Conta(s) Redutora de ICMS:</strong> {item.contasRedutoraIcms || 'não identificada(s)'}.</div>
                              <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mesesConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Competências Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            {/* COR-310: a regra detecta só QUEDA da carga tributária abaixo de 80% da média de
                referência dos 3 meses anteriores — não é uma faixa bilateral de tolerância de
                ±20%. Carga acima da média não é, por si só, anomalia por este critério. */}
            Meses com baseline de 3 meses disponível em que a carga tributária de ICMS não apresentou queda
            abaixo do limite de 80% da média de referência (esta regra verifica apenas redução, não uma faixa
            bilateral de tolerância).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th className='text-end'>Faturamento Base</th>
                  <th className='text-end'>Carga Atual</th>
                  <th className='text-end'>Carga de Referência (3M)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {mesesConciliados.map((item: IConciliadoProvisaoIcms, idx) => {
                  const chave = `icmsc-${item.empresaId}-${item.competencia}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td><strong>{item.competencia}</strong></td>
                        <td className='text-end'>{formatCurrency(item.faturamentoBruto)}</td>
                        <td className='text-end'>{formatPercent(item.cargaTributariaReal)}</td>
                        <td className='text-end'>{formatPercent(item.mediaCargaTributariaReferencia)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaProvisaoIcmsDetalhe;
